'use client'

import { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import Link from 'next/link'
import { memo, useCallback, useState, type FC } from 'react'

import { EditAccountForm } from './EditAccountForm'
import {
  ExportDataParams,
  getListTradingAccountsQueryKey,
  listTradingAccounts,
  ListTradingAccountsParams,
  Tags,
  TradingAccount,
  useGetTradingAccountFilterMetadata,
} from '@api/orval/apiHooks'
import { Any } from '@api/orvalFetcher'
import { BulkActionsMenu } from '@components/BulkActions/BulkActionsMenu'
import { TradingAccountLink } from '@components/TradingAccountLink'
import { createEditAction, createViewAction, TableActions } from '@components/ui/SimpleTable/TableActions'
import { TagsCell } from '@components/ui/Tags/TagCell'
import { PrimeChip } from '@features/primeMode/PrimeChip'
import { buttonVariants, Badge, Dialog, SimpleTooltip } from '@fp/ui-kit'
import { useRealtimeTableSocket } from '@hooks/useRealtimeTableSocket'
import { statusBadgeMapper } from '@lib/badgeMapper'
import { cn } from '@lib/cn'
import { DEFAULT_CURRENCY_USD, TAB_KEYS } from '@lib/constants'
import { getCsv } from '@lib/csv'
import { formatCurrency } from '@lib/formatCurrency'
import { getPhaseBadgeVariant } from '@lib/phaseBadge'
import { getPlatformLabel } from '@lib/platformLabels'
import { sortNumber } from '@lib/sortingHelper'
import { splitAndCapitalFirstLetter, splitAndCapitalizeEachWord } from '@lib/textFormatters'
import { InfiniteTable } from '@ui/InfiniteTable/InfiniteTable'
import { ShowDataAndTooltip } from '@ui/ShowDataAndTooltip'

export const textCenterClassName = '!whitespace-normal !break-words !max-w-[160px] h-full w-full text-center'

const getColumns = (openEditModal: (id: number) => void, tags?: Tags): Array<ColumnDef<TradingAccount>> => [
  {
    accessorKey: 'login',
    header: 'Login',
    size: 120,
    minSize: 120,
    meta: { isDynamic: true },
    cell: ({ row }) => <TradingAccountLink login={row.original.login} />,
  },
  {
    accessorKey: 'name',
    header: 'User',
    minSize: 160,
    maxSize: 160,
    meta: { isDynamic: true, textAlign: 'center' },
    cell: ({ row }) => (
      <Link
        href={`/users/${row.original.user_id}`}
        className={cn(buttonVariants({ variant: 'linkNoPadding' }), textCenterClassName)}
      >
        {row.original.name}
      </Link>
    ),
  },
  {
    accessorKey: 'account_type',
    header: 'Type',
    minSize: 130,
    maxSize: 250,
    meta: { textAlign: 'center' },
    cell: ({ row }) => (
      <div>
        {row.original.account_type ? (
          <Badge variant='outline'>{splitAndCapitalizeEachWord(row.original.account_type)}</Badge>
        ) : (
          '-'
        )}
      </div>
    ),
  },
  {
    accessorKey: 'phase',
    header: 'Phase',
    minSize: 110,
    maxSize: 250,
    meta: { textAlign: 'center' },
    cell: ({ row }) => (
      <div>
        {row.original.phase ? (
          <Badge variant={getPhaseBadgeVariant(row.original.phase)}>
            {splitAndCapitalizeEachWord(row.original.phase)}
          </Badge>
        ) : (
          '-'
        )}
      </div>
    ),
  },
  {
    accessorKey: 'platform',
    header: 'Platform',
    minSize: 110,
    maxSize: 250,
    meta: { textAlign: 'center' },
    cell: ({ row }) => (
      <div>
        {'platform' in row.original && row.original.platform ? (
          <Badge variant='secondary'>{getPlatformLabel(row.original.platform)}</Badge>
        ) : (
          '-'
        )}
      </div>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    minSize: 140,
    maxSize: 250,
    cell: ({ getValue }) => (
      <Badge hasDot variant={statusBadgeMapper.trading_accounts![getValue() as string]}>
        {splitAndCapitalFirstLetter(getValue() as string)}
      </Badge>
    ),
    meta: {
      textAlign: 'center',
    },
  },
  {
    accessorKey: 'tag_list',
    header: 'Tags',
    minSize: 250,
    maxSize: 250,
    cell: ({ row }) => (
      <TagsCell
        resourceId={row.original.id}
        initialTags={row.original.tag_list}
        resourceType='trading_accounts'
        tagList={tags || []}
      />
    ),
    meta: {
      textAlign: 'center',
    },
  },
  {
    accessorKey: 'created_at',
    header: 'Created at',
    size: 160,
    minSize: 160,
    maxSize: 200,
    cell: ({ row }) => <ShowDataAndTooltip data={row.original.created_at} />,
    meta: {
      breakpoint: 'md',
    },
  },
  {
    accessorKey: 'started_at',
    header: 'Started',
    size: 160,
    minSize: 160,
    maxSize: 200,
    cell: ({ row }) => <ShowDataAndTooltip data={row.original.started_at} />,
    meta: {
      breakpoint: 'md',
    },
  },
  {
    accessorKey: 'ended_at',
    header: 'Ended',
    size: 160,
    minSize: 160,
    maxSize: 200,
    cell: ({ row }) => <ShowDataAndTooltip data={row.original.ended_at} />,
  },
  {
    accessorKey: 'account_size_usd',
    header: 'Size',
    size: 120,
    cell: ({ row }) => (
      <SimpleTooltip
        content={<span className='text-xs'>{formatCurrency(row.original.account_size_usd, DEFAULT_CURRENCY_USD)}</span>}
      >
        <span>{formatCurrency(row.original.account_size, row.original.currency)}</span>
      </SimpleTooltip>
    ),

    sortingFn: sortNumber,
  },
  {
    accessorKey: 'balance',
    header: 'Balance',
    size: 100,
    cell: ({ row }) => formatCurrency(row.original.balance || 0, row.original.currency),
  },
  {
    accessorKey: 'profit',
    header: 'Profit',
    size: 100,
    cell: ({ row }) => formatCurrency(row.original.profit || 0, row.original.currency),
  },
  {
    accessorKey: 'equity',
    header: 'Equity',
    size: 100,
    cell: ({ row }) => formatCurrency(row.original.equity || 0, row.original.currency),
  },
  {
    accessorKey: 'payout_count',
    header: 'Payouts Count',
    size: 100,
  },
  {
    accessorKey: 'actions',
    header: 'Actions',
    size: 100,
    cell: ({ row }) => (
      <TableActions
        actions={[
          createEditAction(() => openEditModal(row.original.id)),
          createViewAction(`/accounts/${row.original.id}`),
        ]}
      />
    ),
    maxSize: 100,

    meta: { textAlign: 'center', isHover: true },
  },
]

const EditAccountModal: FC<{
  isEditModalOpen: boolean
  setIsEditModalOpen: (open: boolean) => void
  accountId: number
}> = memo(({ isEditModalOpen, setIsEditModalOpen, accountId }) => (
  <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
    <EditAccountForm accountId={accountId} onCancel={() => setIsEditModalOpen(false)} />
  </Dialog>
))

export const AccountsTable = memo(({ query }: { query: ListTradingAccountsParams }) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [accountId, setAccountId] = useState<number | null>(null)
  const [selectedIds, setSelectedIds] = useState<RowSelectionState>({})

  const { data: filterMeta } = useGetTradingAccountFilterMetadata()

  const tagList = filterMeta?.fields.tags
  const bulkActions = (filterMeta as Any)?.bulk_actions || []

  const onEditAccount = useCallback((id: number) => {
    setAccountId(id)
    setIsEditModalOpen(true)
  }, [])

  const columns = getColumns(onEditAccount, tagList)

  useRealtimeTableSocket({
    socketName: 'tags',
    baseKey: getListTradingAccountsQueryKey(),
    mapPayload: (p: Any) => ({ ...p.data, id: p.data?.resource_id ?? p.data?.id }),
  })

  const getCsvHandle = useCallback(async () => {
    await getCsv(() => {}, {
      model: 'trading_account',
      exporter: 'all_data',
      search_filter: query,
    } as ExportDataParams)
  }, [query])

  return (
    <>
      <InfiniteTable<TradingAccount, ListTradingAccountsParams>
        getQueryKey={getListTradingAccountsQueryKey}
        columns={columns}
        fetchPage={listTradingAccounts}
        query={query}
        tableTitle='Accounts'
        titleSuffix={<PrimeChip />}
        showItemsCount
        filterArea={TAB_KEYS.TRADING_ACCOUNTS}
        csvExportingHandle={getCsvHandle}
        getRowId={(row) => String(row.id) || ''}
        enableRowSelection
        rowSelection={selectedIds}
        onRowSelectionChange={setSelectedIds}
        headerContent={
          bulkActions.length > 0 && (
            <BulkActionsMenu
              bulkActions={bulkActions} // TODO (yulia): remove Any after BE update
              filterArea='trading_accounts'
              selectedIds={selectedIds}
            />
          )
        }
      />

      <EditAccountModal
        isEditModalOpen={isEditModalOpen}
        setIsEditModalOpen={setIsEditModalOpen}
        accountId={accountId!}
      />
    </>
  )
})
